// frontend/src/components/playbook/PatternLibrary.tsx
//
// Playbook §3: the 10 candlestick / chart patterns the engine detects, each
// with a diagram, plain-English meaning, the exact detection rule, and how it
// measured on our coins (patterns.ts → MEASURED). Filter: all / candles /
// chart shapes.

import { useState, type ReactNode } from "react";
import { Alert, Chip } from "@heroui/react";
import { Segmented, Pnl } from "@/components/kit";
import { cn } from "@/lib/utils";
import { Candle } from "./Candle";
import { MEASURED, PATTERNS, STUDY_INFO, type Diagram, type Measured, type PatternBias, type PatternInfo } from "./patterns";

type Filter = "all" | "candle" | "chart";
const FILTERS = [
  { id: "all", label: "All 10" },
  { id: "candle", label: "Candles" },
  { id: "chart", label: "Chart shapes" },
] as const;

const BIAS_COLOR: Record<PatternBias, string> = {
  bullish: "var(--profit)",
  bearish: "var(--loss)",
  neutral: "var(--muted)",
};

const pct = (n: number) => `${Math.round(n * 100)}%`;

export function PatternLibrary() {
  const [filter, setFilter] = useState<Filter>("all");
  const list = PATTERNS.filter((p) => filter === "all" || p.kind === filter);

  return (
    <div>
      <p>
        A pattern is a shape the candles draw that hints at who is winning, buyers or sellers. <B>Candle patterns</B> are
        1–3 candles; <B>chart shapes</B> take dozens. The engine checks for all ten below on every <span className="num">1h</span> close.
        For each one: what it looks like, what it means, the exact rule the code uses, and how often it actually worked on our coins.
      </p>

      <Alert status="warning" className="mt-6">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>On their own, patterns were a coin flip.</Alert.Title>
          <Alert.Description>
            We checked every pattern the engine found on {STUDY_INFO.coins} coins over {STUDY_INFO.days} days
            ({STUDY_INFO.window}). Price went the way the pattern pointed <span className="num">47–54%</span> of the time,
            about the same as a random candle (<span className="num">{pct(STUDY_INFO.anyCandleWay)}</span>). Pattern books
            claim <span className="num">60–75%</span>. Traded on its own, every pattern lost money after fees.
            A pattern tells you <em>when</em>, never <em>whether</em>.
          </Alert.Description>
        </Alert.Content>
      </Alert>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <Segmented aria-label="Pattern type" options={FILTERS} value={filter} onChange={setFilter} />
        <span className="text-sm text-muted">Green = buyers, red = sellers. Faded candles lead in.</span>
      </div>

      <div className="mt-4 space-y-4">
        {list.map((p) => <PatternCard key={p.name} p={p} m={MEASURED[p.name]} />)}
      </div>

      <h3 className="mt-12 text-xl font-semibold tracking-[-0.01em] text-foreground">How the engine uses them</h3>
      <ol className="mt-4 list-decimal space-y-4 pl-6 marker:font-semibold marker:text-muted">
        <li><B>As the trigger in a trend pullback.</B> A hammer, an engulfing candle, or a close past the previous candle's high (the mirror images for shorts) only counts <em>after</em> the 4h trend and the EMA-zone rules pass. This is where a pattern belongs: it times an entry the trend and the location already justify. When the engine's score is a little short, only a pattern trigger can lift it, and the desk then says the trigger must be a hammer or engulfing.</li>
        <li><B>As one vote in the confluence score.</B> Patterns are one of six votes (with regime, indicators, Fibonacci, Wyckoff and Elliott), worth <span className="num">15–25%</span> of the score depending on the market regime. A doji casts no vote.</li>
        <li><B>For the plan's levels.</B> When a chart shape is confirmed, its stop can become the trade's stop (if it sits on the right side of the entry) and its measured-move target can push the second target further.</li>
      </ol>

      <h3 className="mt-10 text-xl font-semibold tracking-[-0.01em] text-foreground">What to do with this</h3>
      <ul className="mt-4 list-disc space-y-4 pl-6 marker:text-muted">
        <li><B>Never trade a pattern by itself.</B> A hammer in the middle of nowhere is just a candle. First the trend, then the place (a zone, a floor, a ceiling), and only then the pattern.</li>
        <li><B>Wait for the close.</B> A hammer at <span className="num">:45</span> can still close as a big red candle at <span className="num">:00</span>. Patterns exist only on finished candles.</li>
        <li><B>Matching the trend didn't rescue them.</B> Hammers in an uptrend went their way <span className="num">51%</span> of the time; shooting stars in a downtrend <span className="num">46%</span>. The trend helps only together with the zone and the plan's rules.</li>
        <li><B>Ignore the percentages in pattern books.</B> They come from other markets, other years and no fees. On these coins nothing beat <span className="num">54%</span>.</li>
      </ul>

      <details className="group mt-8 rounded-xl border border-border bg-surface px-5 py-4 text-[0.9375rem]">
        <summary className="cursor-pointer select-none font-sans text-base font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-focus">
          How we tested
        </summary>
        <div className="mt-3 space-y-3">
          <p>
            For every closed <span className="num">1h</span> candle we ran the engine's own detectors on the 200 candles before it (no peeking ahead),
            across {STUDY_INFO.coins} large coins ({STUDY_INFO.candles.toLocaleString()} candles). A chart shape is counted once, on the candle that confirms it.
          </p>
          <p>
            <B>Went its way:</B> after the close, did price move one ATR (a typical hour's range) in the pattern's direction before
            one ATR against it, within 24 hours? A coin flip scores about <span className="num">50%</span>.
          </p>
          <p>
            <B>Traded alone:</B> enter at the next open, stop <span className="num">1.5</span> ATR away, take half at <span className="num">1.5R</span> and move the stop to break-even,
            the rest at <span className="num">2.5R</span>, close after 24 hours. Fees, slippage and funding included. For comparison, a long on <em>any</em> candle
            made <Pnl value={STUDY_INFO.anyLongR} suffix="R" /> and a short <Pnl value={STUDY_INFO.anyShortR} suffix="R" />.
          </p>
          <p>
            Re-run it: <code className="num rounded bg-default px-1.5 py-0.5 text-[0.8125rem] text-foreground">{STUDY_INFO.rerun}</code> in <code className="num text-[0.8125rem]">backend/</code>.
          </p>
        </div>
      </details>
    </div>
  );
}

// ─── card ────────────────────────────────────────────────────────────────────

function PatternCard({ p, m }: { p: PatternInfo; m?: Measured }) {
  return (
    <article
      className="overflow-hidden rounded-2xl border border-border bg-surface sm:grid sm:grid-cols-[15rem_minmax(0,1fr)]"
      aria-labelledby={`pat-${slug(p.name)}`}
    >
      {/* Left: the shape, and what it measured — picture and numbers side by side with the words. */}
      <div className="flex flex-col gap-4 border-b border-separator bg-background/50 p-4 sm:border-b-0 sm:border-r">
        <PatternDiagram d={p.diagram} bias={p.bias} name={p.name} />
        {m && <div className="hidden sm:block"><MeasuredList m={m} textbook={p.textbook} /></div>}
      </div>
      <div className="flex min-w-0 flex-col gap-3.5 p-5 text-[0.9375rem] leading-relaxed">
        <header className="flex flex-wrap items-center gap-2">
          <h3 id={`pat-${slug(p.name)}`} className="text-lg font-semibold tracking-[-0.01em] text-foreground">{p.name}</h3>
          <BiasTag bias={p.bias} />
          <span className="text-[0.8125rem] text-muted">{p.size}</span>
          {p.trigger && <Chip size="sm" variant="soft" color="accent" className="ml-auto font-semibold">Entry trigger</Chip>}
        </header>
        <p>{p.says}</p>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">How the engine spots it</div>
          <ul className="mt-1.5 list-disc space-y-1 pl-5 marker:text-muted">
            {p.rules.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </div>
        <p><B>Where it matters.</B> {p.where}</p>
        {m?.note && <p className="text-sm text-muted">{m.note}</p>}
        {/* Phones: the numbers come after the name and the words, not before. */}
        {m && <div className="mt-1 border-t border-separator pt-4 sm:hidden"><MeasuredList m={m} textbook={p.textbook} /></div>}
      </div>
    </article>
  );
}

function MeasuredList({ m, textbook }: { m: Measured; textbook: number }) {
  return (
    <div className="text-[0.8125rem]">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted">On our coins</div>
      <div className="mt-2 divide-y divide-separator">
        <StatRow
          label="Went its way"
          hint="a coin flip is ≈ 50%"
          below={m.wentItsWay != null && <WayMeter value={m.wentItsWay} />}
        >
          {m.wentItsWay == null ? <span className="text-muted">—</span> : <span className="text-foreground">{pct(m.wentItsWay)}</span>}
        </StatRow>
        <StatRow label="Books claim" hint="hard-coded in the detector">
          <span className="text-muted">{pct(textbook)}</span>
        </StatRow>
        <StatRow label="Traded alone" hint="per trade, after fees">
          {m.netR == null ? <span className="text-muted">—</span> : <Pnl value={m.netR} suffix="R" />}
        </StatRow>
      </div>
      <p className="mt-2 text-xs text-muted">
        Seen <span className="num text-foreground">{m.seen.toLocaleString()}</span> times · {STUDY_INFO.coins} coins · {STUDY_INFO.days} days
      </p>
    </div>
  );
}

function StatRow({ label, hint, below, children }: { label: string; hint: string; below?: ReactNode; children: ReactNode }) {
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <span className="text-foreground">{label}</span>
          <span className="block text-xs leading-snug text-muted">{hint}</span>
        </div>
        <span className="num shrink-0 text-base font-semibold">{children}</span>
      </div>
      {below}
    </div>
  );
}

/** A thin 0–100% bar with a tick at 50% (the coin flip). */
function WayMeter({ value }: { value: number }) {
  return (
    <span className="relative mt-1.5 block h-1 w-full rounded-full bg-default" aria-hidden>
      <span className="absolute inset-y-0 left-0 rounded-full bg-foreground/60" style={{ width: `${Math.round(value * 100)}%` }} />
      <span className="absolute -top-0.5 left-1/2 h-2 w-px bg-foreground" />
    </span>
  );
}

function BiasTag({ bias }: { bias: PatternBias }) {
  const label = bias === "bullish" ? "Bullish" : bias === "bearish" ? "Bearish" : "No direction";
  return (
    <span
      className={cn(
        "rounded-full px-2.5 py-0.5 text-xs font-semibold",
        bias === "bullish" && "bg-profit-soft text-profit",
        bias === "bearish" && "bg-loss-soft text-loss",
        bias === "neutral" && "bg-default text-muted",
      )}
    >
      {label}
    </span>
  );
}

function B({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-foreground">{children}</strong>;
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "-");

// ─── diagrams (inline SVG; prices live in a 0–100 space, up = higher) ─────────

const W = 240;
const H = 132;

function PatternDiagram({ d, bias, name }: { d: Diagram; bias: PatternBias; name: string }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={`${name} diagram`}>
      {d.type === "candles" ? <CandleDiagram d={d} bias={bias} /> : <LineDiagram d={d} bias={bias} />}
    </svg>
  );
}

function CandleDiagram({ d, bias }: { d: Extract<Diagram, { type: "candles" }>; bias: PatternBias }) {
  const top = 12, bottom = 24;
  // Fit the bars' own high/low to the box so the candles fill it.
  const hiP = Math.max(...d.bars.map((b) => b[1]));
  const loP = Math.min(...d.bars.map((b) => b[2]));
  const y = (p: number) => top + ((hiP - p) / (hiP - loP || 1)) * (H - top - bottom);
  const step = 38;
  const x0 = (W - (d.bars.length - 1) * step) / 2;
  const [h0, h1] = d.highlight;
  const color = BIAS_COLOR[bias];
  return (
    <g>
      <rect x={x0 + h0 * step - 17} y={4} width={(h1 - h0) * step + 34} height={H - bottom + 2} rx={7} fill={color} opacity={0.1} />
      {d.bars.map(([o, hi, lo, c], i) => (
        <g key={i} opacity={i >= h0 && i <= h1 ? 1 : 0.4}>
          <Candle x={x0 + i * step} o={y(o)} c={y(c)} h={y(hi)} l={y(lo)} w={18} />
        </g>
      ))}
      <text x={x0 + ((h0 + h1) / 2) * step} y={H - 4} fontSize={12} fontWeight={600} fill={color} textAnchor="middle">
        {d.label}
      </text>
    </g>
  );
}

function LineDiagram({ d, bias }: { d: Extract<Diagram, { type: "line" }>; bias: PatternBias }) {
  const top = 18, bottom = 16, left = 6, lane = 64;
  const y = (p: number) => top + ((100 - p) / 100) * (H - top - bottom);
  const x = (i: number) => left + (i * (W - left - lane)) / (d.points.length - 1);
  const path = (from: number, to: number) =>
    d.points.slice(from, to + 1).map((p, k) => `${k === 0 ? "M" : "L"}${x(from + k).toFixed(1)} ${y(p).toFixed(1)}`).join(" ");
  const pt = (i: number) => d.points[i] ?? d.neckline;
  const color = BIAS_COLOR[bias];
  const right = W - lane + 4;
  const level = (p: number, stroke: string, label: string, from = left) => (
    <g>
      <line x1={from} x2={right} y1={y(p)} y2={y(p)} stroke={stroke} strokeDasharray="3 2" strokeWidth={1} />
      <text x={right + 3} y={y(p) + 4} fontSize={11} fill={stroke}>{label}</text>
    </g>
  );
  const isPeak = (i: number) => pt(i) >= (d.points[i - 1] ?? -Infinity) && pt(i) >= (d.points[i + 1] ?? -Infinity);

  return (
    <g>
      {level(d.neckline, "var(--muted)", "neckline")}
      {level(d.target, "var(--profit)", "target", x(d.breakAt))}
      {level(d.stop, "var(--loss)", "stop", x(d.breakAt))}
      <path d={path(0, d.breakAt)} fill="none" stroke="var(--foreground)" strokeWidth={1.6} strokeLinejoin="round" opacity={0.85} />
      <path d={path(d.breakAt, d.points.length - 1)} fill="none" stroke={color} strokeWidth={1.6} strokeDasharray="4 3" strokeLinejoin="round" />
      {d.marks.map((mk) => (
        <text
          key={mk.i}
          x={x(mk.i)}
          y={isPeak(mk.i) ? y(pt(mk.i)) - 6 : y(pt(mk.i)) + 14}
          fontSize={11}
          fill="var(--muted)"
          textAnchor="middle"
        >
          {mk.label}
        </text>
      ))}
      <circle cx={x(d.breakAt)} cy={y(pt(d.breakAt))} r={3.2} fill={color} />
      {/* Left of the break, on the side of the neckline the price path left empty. */}
      <text
        x={x(d.breakAt) - 6}
        y={y(pt(d.breakAt)) + (bias === "bullish" ? -7 : 15)}
        fontSize={11}
        fontWeight={600}
        fill={color}
        textAnchor="end"
      >
        {bias === "bullish" ? "close above" : "close below"}
      </text>
    </g>
  );
}
